import React, { useState, useRef, useEffect, useMemo, useContext } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";
import CheckBoxList from "../../common/CheckBoxList";
import RadioButtonGroup from "../../common/RadioButtonGroup";
import { MultipleChoiceOptionStringId } from "../../../types/forms/MultipleChoiceOptions";
import UserContext from "../../../state/UserContext";

export interface StatusOption {
  key: string;
  label: string;
  hasDateRange?: boolean;
}

export interface SelectedStatus {
  key: string;
  dateRange?: "12months" | "alltime";
}

interface AdvTaskStatusMultiSelectProps {
  selectedStatuses: SelectedStatus[];
  onSelectionChange: (selectedStatuses: SelectedStatus[]) => void;
  className?: string;
}

const AdvTaskStatusMultiSelect: React.FC<AdvTaskStatusMultiSelectProps> = ({
  selectedStatuses,
  onSelectionChange,
  className = "",
}) => {
  const userContext = useContext(UserContext);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const statusOptions: StatusOption[] = [
    { key: "ACTIVE", label: "Active" },
    { key: "OVERDUE", label: "Overdue" },
    { key: "COMPLETED", label: "Completed", hasDateRange: true },
    { key: "CANCELLED", label: "Cancelled" },
    { key: "HISTORIC", label: "Historic" },
  ];

  // Transform status options to CheckBoxList format
  const statusCheckboxOptions: MultipleChoiceOptionStringId[] = useMemo(() => {
    return statusOptions.map((option) => ({
      optionId: option.key,
      text: option.label,
      value: option.key,
      isSelected: selectedStatuses.some((s) => s.key === option.key),
      allowCustomText: false,
      translateDisplayValue: false,
      rawDisplayValue: option.label,
      displayValueTranslationKeyIdentifier: null,
      categoryId: null,
    }));
  }, [selectedStatuses]);

  // Date range options for completed status
  const dateRangeOptions: MultipleChoiceOptionStringId[] = useMemo(() => {
    const completedStatus = selectedStatuses.find((s) => s.key === "COMPLETED");
    const currentDateRange = completedStatus?.dateRange || "alltime";

    return [
      {
        optionId: "12months",
        text: "Last 12 months",
        value: "12months",
        isSelected: currentDateRange === "12months",
        allowCustomText: false,
        translateDisplayValue: false,
        rawDisplayValue: "Last 12 months",
        displayValueTranslationKeyIdentifier: null,
        categoryId: null,
      },
      {
        optionId: "alltime",
        text: "All time",
        value: "alltime",
        isSelected: currentDateRange === "alltime",
        allowCustomText: false,
        translateDisplayValue: false,
        rawDisplayValue: "All time",
        displayValueTranslationKeyIdentifier: null,
        categoryId: null,
      },
    ];
  }, [selectedStatuses]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getDisplayText = (): string => {
    if (selectedStatuses.length === 0) return "All Statuses";

    // Check if all are selected
    if (selectedStatuses.length === statusOptions.length) {
      return "All Statuses";
    }

    // Two options selected
    if (selectedStatuses.length === 2) {
      const labels = selectedStatuses
        .map((s) => statusOptions.find((opt) => opt.key === s.key)?.label)
        .filter(Boolean);
      return labels.join(" & ");
    }

    // Three or more options selected
    if (selectedStatuses.length >= 3) {
      return `${selectedStatuses.length} Statuses`;
    }

    // One option selected
    if (selectedStatuses.length === 1) {
      const label = statusOptions.find(
        (opt) => opt.key === selectedStatuses[0].key,
      )?.label;
      return `${label} only`;
    }

    return "Select Statuses";
  };

  // Handle status checkbox changes
  const handleStatusChange = (newOptions: MultipleChoiceOptionStringId[]) => {
    const newSelection: SelectedStatus[] = [];

    newOptions.forEach((option) => {
      if (option.isSelected) {
        const statusOption = statusOptions.find(
          (opt) => opt.key === option.optionId,
        );
        newSelection.push({
          key: option.optionId,
          dateRange: statusOption?.hasDateRange ? "alltime" : undefined,
        });
      }
    });

    onSelectionChange(newSelection);
  };

  // Handle date range radio button changes
  const handleDateRangeChange = (
    newOptions: MultipleChoiceOptionStringId[],
  ) => {
    const selectedDateRange = newOptions.find((opt) => opt.isSelected);
    if (selectedDateRange) {
      const updatedSelection = selectedStatuses.map((s) =>
        s.key === "COMPLETED"
          ? {
              ...s,
              dateRange: selectedDateRange.optionId as "12months" | "alltime",
            }
          : s,
      );
      onSelectionChange(updatedSelection);
    }
  };

  const isStatusSelected = (statusKey: string): boolean => {
    return selectedStatuses.some((s) => s.key === statusKey);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        className="w-full sm:w-48 px-2.5 py-1.5 text-sm border border-gray-200 rounded bg-white text-left focus:outline-none focus:ring-0 focus:border-gray-300 flex items-center justify-between"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="truncate">{getDisplayText()}</span>
        <FontAwesomeIcon
          icon={faChevronDown}
          className={`ml-2 text-xs transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded shadow-lg z-50 max-h-80 overflow-y-auto p-3">
          {/* Status Selection Checkboxes */}
          <CheckBoxList<MultipleChoiceOptionStringId>
            uniqueFieldName="adv-task-status-select"
            values={statusCheckboxOptions}
            onChange={handleStatusChange}
            displayMode="vertical"
            selectMinCount={null}
            selectMaxCount={null}
            textColourClassName="text-gray-700"
            iconColourClassName="text-white"
            checkboxBorderClassName="border border-gray-300"
            checkboxCheckedBgColourHexCode={
              userContext.user?.client?.primaryHexColour
            }
            checkboxUncheckedBgColourClassName="bg-white"
            isReadOnly={false}
          />

          {/* Date Range Selection for Completed Status */}
          {isStatusSelected("COMPLETED") && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <RadioButtonGroup<MultipleChoiceOptionStringId>
                uniqueFieldName="adv-task-date-range"
                fieldLabel="Completed tasks:"
                values={dateRangeOptions}
                onChange={handleDateRangeChange}
                containerClassNames="mt-2 space-y-1 !ml-0"
                labelClassNames="text-xs text-gray-700"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdvTaskStatusMultiSelect;
